export const articles = [
  {
    id: 'livestream-gmv-reality',
    title: '直播电商退潮之后：从 GMV 神话回到「真实履约」',
    categoryId: 'industry',
    categoryName: '行业起底',
    excerpt:
      '当退货率、履约成本与库存周转被摆到台面上，直播电商这笔账其实一直都不好算。我们整理了近三年的公开数据，试着还原它真实的利润结构。',
    cover:
      'https://readdy.ai/api/search-image?query=warm%20amber%20ring%20light%20and%20shipping%20boxes%20in%20a%20studio%20abstract%20editorial%20photography%20with%20soft%20dramatic%20lighting%20on%20cream%20background%2C%20high%20detail&width=1200&height=800&seq=bl-art-live&orientation=landscape',
    author: '沈亦',
    date: '2026-09-11',
    readTime: '12 分钟',
    heat: 9812,
    tags: ['直播电商', '商业模式', '履约成本'],
    featured: true,
    keyPoints: [
      '头部直播间的高 GMV 并不等于高利润，退货与履约成本是最大的隐性黑洞。',
      '中小商家的真实困境不是卖不动，而是卖动了也不一定赚钱。',
      '行业正在从「冲销量」转向「算净利」，这是一次健康但痛苦的出清。',
    ],
    sections: [
      {
        heading: '一、GMV 只是一个漂亮的分子',
        paragraphs: [
          '直播电商最常被引用的数字是 GMV，也就是成交总额。但它其实只是一个分子，分母包含了退货、履约、坑位费、佣金和平台扣点。多份公开行业报告都指出，服装与美妆品类的直播退货率显著高于传统电商，部分细分品类在促销节点的退货比例甚至接近一半。',
          '把退货算进去之后，很多看起来热闹的「破亿专场」实际成交额会明显缩水。这不是某个主播的问题，而是这个模式的结构性特征：冲动消费的比例越高，退货的弹性就越大。',
        ],
      },
      {
        heading: '二、被忽略的履约账单',
        paragraphs: [
          '仓储、打包、逆向物流和二次质检，构成了直播电商最沉默的成本项。传统电商的订单分布相对均匀，而直播间往往在短短几小时内涌出巨量订单，商家必须为峰值备货、备人、备仓，这些固定投入在平常时段是闲置的。',
          '这也解释了为什么很多白牌商家在第一次爆单后反而现金流紧张：备货占用了大量资金，退货又延迟了回款，账期一拉长，利润就被时间吃掉了。',
        ],
      },
      {
        heading: '三、商家真正的选择',
        paragraphs: [
          '我们在公开访谈和财报里反复看到同一个转向：越来越多商家开始把直播间当成「测款和种草」的渠道，而不是唯一的成交场。用直播验证爆款，再把稳定订单沉淀到自有渠道，成为一个更可持续的组合。',
          '这意味行业评价标准正在改变。过去看单场 GMV，现在看复购率和净利率。对平台和主播来说，这是一次不那么好看的转型，但对整个生态来说，把生意做回生意本来的样子，未必是坏事。',
        ],
      },
      {
        heading: '四、接下来该观察什么',
        paragraphs: [
          '我们建议读者接下来重点关注三个公开指标：退货率的变化趋势、头部机构自营供应链的投入比例，以及平台对头部主播的流量分配策略调整。这三个指标能相对完整地反映行业的真实温度。',
          '需要说明的是，本文引用的多份数据来自不同机构的公开报告，口径并不完全一致，我们只做趋势解读，不对单一口径做绝对化判断。',
        ],
      },
    ],
  },
  {
    id: 'brand-split-capital-logic',
    title: '一个品牌拆成两个：老国货「分家」背后的资本算术',
    categoryId: 'brand',
    categoryName: '品牌扒皮',
    excerpt:
      '表面上是战略聚焦，实际上常常是资本结构与渠道矛盾到了必须切割的程度。我们梳理了几次公开披露的「分家」案例，试着看懂这类操作真正解决了什么问题。',
    cover:
      'https://readdy.ai/api/search-image?query=warm%20amber%20two%20vintage%20product%20packages%20being%20separated%20on%20a%20cream%20desk%20abstract%20editorial%20flatlay%20with%20soft%20shadows%2C%20high%20detail&width=1200&height=800&seq=bl-art-brand&orientation=landscape',
    author: '林砚舟',
    date: '2026-09-09',
    readTime: '10 分钟',
    heat: 8420,
    tags: ['品牌战略', '资本结构', '渠道'],
    featured: true,
    keyPoints: [
      '品牌分家通常不是营销决定，而是股东与渠道利益无法再协调的结果。',
      '拆分的真正收益往往体现在估值与融资上，而不是消费者端的体验改善。',
      '对老品牌而言，最难分的是「用户认知」这份无形资产。',
    ],
    sections: [
      {
        heading: '一、公告里的两套语言',
        paragraphs: [
          '几乎所有品牌分家的公告都会使用同一套措辞：聚焦主业、独立发展、提升决策效率。这些表述并不算错，但它们省略了更具体的原因——股东之间对公司方向的判断出现了分歧，或者不同渠道的利益已经无法在同一套体系里并存。',
          '把公告和后续的工商变更信息对照着看，通常能看出更清楚的轮廓：原公司保留了什么资产，新主体承接了哪些负债与团队。',
        ],
      },
      {
        heading: '二、渠道矛盾往往是被低估的导火索',
        paragraphs: [
          '当同一个品牌既要维持线下的价格体系，又要承受线上直播的低价冲击，内部冲突几乎是必然的。线下经销商需要稳定的价差，线上渠道需要更低的到手价，两者很难同时被满足。',
          '分割成两个品牌，各走各的价格带，是很多公司选择的折中方案。它牺牲了规模效应，换来的是渠道之间不再互相消耗。',
        ],
      },
      {
        heading: '三、最难切开的是用户认知',
        paragraphs: [
          '产品和团队可以按清单切割，但消费者心里的那个品牌印象没法一刀两断。老用户往往会问：我熟悉的那款产品到底归谁？这个问题如果处理不好，分家带来的不是各自增长，而是共同的信任流失。',
          '因此公开案例里，运营得比较平稳的分家都会在过渡期保持产品线稳定，并把「哪些变了、哪些没变」讲清楚。',
        ],
      },
      {
        heading: '四、对普通读者的意义',
        paragraphs: [
          '看懂品牌分家，能帮助我们理解货架上一件商品为什么换了包装、为什么价格悄悄变了。它不是阴谋，但也确实是一道需要被读懂的商业算术。',
        ],
      },
    ],
  },
  {
    id: 'rumor-72-hours',
    title: '一条谣言的 72 小时：完整复盘它的传播链条',
    categoryId: 'society',
    categoryName: '社会观察',
    excerpt:
      '我们追踪了一条在社交平台广泛流传的说法，从它出现到被辟谣，逐小时记录每个节点的转发结构。结果比谣言本身更值得警惕。',
    cover:
      'https://readdy.ai/api/search-image?query=warm%20amber%20glowing%20smartphone%20screens%20and%20network%20lines%20abstract%20editorial%20photography%20on%20dark%20cream%20background%20with%20deep%20green%20accents%2C%20high%20detail&width=1200&height=800&seq=bl-art-rumor&orientation=landscape',
    author: '顾南',
    date: '2026-09-07',
    readTime: '14 分钟',
    heat: 10240,
    tags: ['信息传播', '核实', '网络生态'],
    featured: true,
    keyPoints: [
      '谣言的第一跳往往来自截取片段，而非凭空捏造。',
      '情绪化的标题会显著提升二次转发的概率，与真假无关。',
      '辟谣的传播半径通常只有原谣言的一小部分，这是关键的不对称。',
    ],
    sections: [
      {
        heading: '一、起点：一个被截断的片段',
        paragraphs: [
          '这条说法最初出现在一个本地讨论帖里，配图是一段被截取的聊天记录。原始语境中，它只是一个未经确认的转述；但在截图里，转述者被抹掉了，只留下结论式的语气。',
          '这是绝大多数误传的共同起点：不是凭空编造，而是把「有人说」悄悄变成「就是这样」。',
        ],
      },
      {
        heading: '二、6 到 18 小时：情绪的加速器',
        paragraphs: [
          '在第一个大号转发后，内容的标题被改成了更强的语气，正文缩到三行以内。我们统计到的转发高峰正好出现在这个阶段，转发的用户里，超过一半没有点开原始帖子的上下文。',
          '值得注意的是，同一时间也有人提出质疑，但这些质疑的出现时间普遍晚于情绪化转发，且转发量低了一个数量级。',
        ],
      },
      {
        heading: '三、24 到 60 小时：辟谣的不对称',
        paragraphs: [
          '当相关方给出正式说明后，辟谣信息的传播量只有原说法的一小部分。原因并不复杂：澄清需要解释背景、需要读完，而原说法只需要一个情绪。',
          '我们在整理数据时还发现，部分账号在辟谣后仍继续使用原说法配图，形成了「旧图新用」的二次传播，这进一步拉长了谣言的尾巴。',
        ],
      },
      {
        heading: '四、我们能做的一件小事',
        paragraphs: [
          '在看到一条令人惊讶的信息时，先找它的原始出处，再看时间是否对得上。这两步花不了两分钟，却能拦下大多数误传。',
          '本文记录的时间与转发数据均来自公开平台的可见信息，我们隐去了具体账号，目的是梳理传播结构，而不是指责任何个人。',
        ],
      },
    ],
  },
  {
    id: 'fandom-data-factory',
    title: '打投、控评与「数据女工」：追星经济如何被组织起来',
    categoryId: 'entertainment',
    categoryName: '娱乐深扒',
    excerpt:
      '粉丝的狂热很少是自发的。我们拆解了应援组织的分工结构，看清一套高度工业化的协作体系是如何运转的。',
    cover:
      'https://readdy.ai/api/search-image?query=warm%20amber%20glow%20sticks%20and%20smartphones%20in%20a%20dark%20concert%20hall%20abstract%20editorial%20photography%20with%20deep%20green%20and%20gold%20accents%2C%20high%20detail&width=1200&height=800&seq=bl-art-fandom&orientation=landscape',
    author: '苏黎',
    date: '2026-09-05',
    readTime: '11 分钟',
    heat: 7633,
    tags: ['粉丝经济', '组织协作', '平台机制'],
    featured: false,
    keyPoints: [
      '应援组织内部存在清晰的分工，效率接近一个小型运营团队。',
      '打投行为的驱动力来自平台的排名机制，而非单纯的情感。',
      '理解这套机制，才能理解粉丝为何常感到疲惫却又无法退出。',
    ],
    sections: [
      {
        heading: '一、被误解的「自发热情」',
        paragraphs: [
          '外界常把粉丝行为理解为非理性冲动，但实际观察会发现，很多应援组织内部有明确的分组：数据组、文案组、财务组和物资组，甚至还有轮班表。这更像一个运转中的项目团队。',
          '热情是燃料，但让机器持续运转的，是一套被反复验证过的协作流程。',
        ],
      },
      {
        heading: '二、平台机制才是真正的指挥棒',
        paragraphs: [
          '打投之所以重要，是因为平台的榜单和曝光位与数据强绑定。只要排名直接影响资源分配，数据竞争就不会停。换句话说，粉丝在回应机制，而机制由平台设定。',
          '这也解释了为什么「理性追星」的呼吁收效有限：个体可以退出，但结构性的激励一直存在。',
        ],
      },
      {
        heading: '三、被消耗的时间与金钱',
        paragraphs: [
          '公开报道中，粉丝为应援进行的集中消费规模可观，而组织内部的账目公开程度参差不齐。当资金规模上升，信任问题就开始出现，这也是近年来多次应援争议的核心。',
          '对参与者来说，最需要被看见的其实不是付出，而是退出成本——一旦投入，退出就意味着「辜负」，这是一种很难挣脱的心理结构。',
        ],
      },
      {
        heading: '四、更健康的可能',
        paragraphs: [
          '行业里也出现过更温和的样本：把组织成本透明化，把精力从数据打投转向内容创作与公益。它们未必更「热闹」，但更可持续。',
        ],
      },
    ],
  },
  {
    id: 'short-video-hook-formula',
    title: '短视频的「爽感」配方：3 秒抓人背后的算法与心理',
    categoryId: 'culture',
    categoryName: '文化解码',
    excerpt:
      '为什么有些视频你明知道没什么内容，却还是刷了两分钟？这背后有一套可以被拆解的注意力设计。',
    cover:
      'https://readdy.ai/api/search-image?query=warm%20amber%20vertical%20phone%20with%20abstract%20motion%20and%20light%20trails%20on%20cream%20desk%20editorial%20photography%20with%20deep%20green%20accents%2C%20high%20detail&width=1200&height=800&seq=bl-art-short&orientation=landscape',
    author: '郑野',
    date: '2026-09-03',
    readTime: '9 分钟',
    heat: 6901,
    tags: ['注意力', '内容设计', '算法'],
    featured: false,
    keyPoints: [
      '前 3 秒抛出的悬念，是决定完播率的关键变量。',
      '算法奖励完播与互动，创作者因此被激励去做更强的情绪刺激。',
      '理解这套配方，能帮我们更主动地选择看什么。',
    ],
    sections: [
      {
        heading: '一、黄金三秒不是玄学',
        paragraphs: [
          '大量内容培训课程都会强调开头三秒，原因很直接：平台的推荐机制高度依赖完播率，而用户是否继续观看几乎在这几秒内决定。',
          '于是开头被设计成「信息缺口」——一个没有立刻给出答案的问题、一个反常识的结论，或一个强烈情绪的瞬间。',
        ],
      },
      {
        heading: '二、情绪强度的军备竞赛',
        paragraphs: [
          '当所有人都学会了抛悬念，悬念就得越来越强。这是典型的激励扭曲：算法本身没有恶意，但它奖励的行为会被创作者不断放大。',
          '长期来看，这会让内容趋向同质化，因为真正需要耐心的深度表达在这种机制下天然吃亏。',
        ],
      },
      {
        heading: '三、作为观众，我们能做什么',
        paragraphs: [
          '最有效的办法是主动设定观看意图：带着问题去找内容，而不是打开就随波逐流。这听起来简单，但能显著改变刷到的东西。',
          '另一件小事是留意自己的「后悔时刻」——刷完之后感到空虚的那个瞬间，它往往就是注意力被拿走的证据。',
        ],
      },
    ],
  },
  {
    id: 'mcn-contract-hidden-terms',
    title: 'MCN 合约里的隐形条款：新人博主为何总是先红后穷',
    categoryId: 'industry',
    categoryName: '行业起底',
    excerpt:
      '走红之后反而陷入困境，并不是偶然。我们把常见的合约结构拆开，看看收益是如何在各个环节被稀释的。',
    cover:
      'https://readdy.ai/api/search-image?query=warm%20amber%20signed%20contract%20papers%20and%20camera%20lens%20on%20cream%20desk%20abstract%20editorial%20photography%20with%20soft%20shadows%2C%20high%20detail&width=1200&height=800&seq=bl-art-mcn&orientation=landscape',
    author: '何知远',
    date: '2026-09-01',
    readTime: '10 分钟',
    heat: 6488,
    tags: ['创作者经济', '合约', '分成'],
    featured: false,
    keyPoints: [
      '分成比例之外，账号归属与解约条件是更关键的两条。',
      '预付与垫资看起来是支持，实际上会变成长期的收益陷阱。',
      '签约前把「分手条款」读清楚，比谈分成比例更重要。',
    ],
    sections: [
      {
        heading: '一、分成比例只是冰山一角',
        paragraphs: [
          '新人最关心的往往是五五分还是七三分，但真正决定长期收益的是账号归属、内容版权和竞业限制。如果账号主体归机构，博主在解约时可能失去全部粉丝资产。',
          '这也是很多纠纷的根源：走红后想独立，却发现「自己」并不属于自己。',
        ],
      },
      {
        heading: '二、预付的本质是债务',
        paragraphs: [
          '机构提供的签约金、设备与运营投入，通常会以「预付」或「垫资」的形式存在，并从后续收益里优先扣除。在收入不稳的早期，这会让创作者长期处于还债状态。',
          '理解这一点很重要：预付不是奖励，而是被推迟的、带条件的收入。',
        ],
      },
      {
        heading: '三、被忽视的退出成本',
        paragraphs: [
          '违约金计算方式、解约通知期、竞业范围，这三项决定了你能不能体面地离开。公开案例里，很多争议最终都落在这一条上。',
          '一个实用的建议是：把最坏情况写下来——如果合作终止，我还能带走什么？如果答案是「几乎什么都没有」，那这份合约需要重新考虑。',
        ],
      },
    ],
  },
  {
    id: 'nostalgia-marketing-engine',
    title: '怀旧为何总是好卖：情绪是怎么被做成生意的',
    categoryId: 'brand',
    categoryName: '品牌扒皮',
    excerpt:
      '复刻包装、重播老歌、翻拍经典，怀旧营销几乎从不失手。我们想弄清，这份情绪到底被谁、以什么方式兑现成了销量。',
    cover:
      'https://readdy.ai/api/search-image?query=warm%20amber%20retro%20packaging%20and%20old%20photographs%20on%20cream%20desk%20abstract%20editorial%20flatlay%20with%20soft%20gold%20light%2C%20high%20detail&width=1200&height=800&seq=bl-art-nostalgia&orientation=landscape',
    author: '叶知秋',
    date: '2026-08-29',
    readTime: '8 分钟',
    heat: 5920,
    tags: ['怀旧营销', '消费心理', '品牌'],
    featured: false,
    keyPoints: [
      '怀旧提供的是心理安全感，这比新鲜感更稳定、更易被触发。',
      '复刻的成本低、风险小，因此成为品牌的常规安全牌。',
      '但过度使用会让怀旧贬值，消费者终会识别出套路。',
    ],
    sections: [
      {
        heading: '一、怀旧卖的是一种确定感',
        paragraphs: [
          '在变化太快的环境里，人们会更怀念过去。怀旧营销之所以稳定有效，是因为它提供的不是产品本身，而是一种「我知道这是好的」的确定感。',
          '确定感无需教育成本，品牌只需要唤起记忆，剩下的信任由消费者的过去完成。',
        ],
      },
      {
        heading: '二、低成本的安全牌',
        paragraphs: [
          '对品牌而言，复刻一款经典产品比开发一款新品风险低得多：模具、配方和用户认知都已存在，营销只需强调「回归」即可。',
          '也正因为门槛低，怀旧很快从创意变成了套路。当所有品牌都在复刻，怀旧本身的稀缺性就被消耗掉了。',
        ],
      },
      {
        heading: '三、什么时候怀旧会失效',
        paragraphs: [
          '当复刻产品的品质不如从前，消费者会感到被辜负，这种失望比普通的产品不满更强烈，因为它同时伤害了记忆。',
          '因此真正做得好的怀旧营销，往往在情怀之外还拿出了实打实的品质升级，二者缺一不可。',
        ],
      },
    ],
  },
  {
    id: 'small-content-team-survival',
    title: '「数据很好看，生意很难做」：一个小内容团队的生存复盘',
    categoryId: 'society',
    categoryName: '社会观察',
    excerpt:
      '八个内容团队，三种结局。我们和几位小型内容工作室的负责人聊了聊，记录下流量繁荣背后真实的经营处境。',
    cover:
      'https://readdy.ai/api/search-image?query=warm%20amber%20small%20studio%20desk%20with%20laptop%20and%20analysis%20charts%20on%20cream%20background%20abstract%20editorial%20photography%20with%20soft%20shadows%2C%20high%20detail&width=1200&height=800&seq=bl-art-team&orientation=landscape',
    author: '沈亦',
    date: '2026-08-27',
    readTime: '11 分钟',
    heat: 5316,
    tags: ['内容创业', '经营', '流量'],
    featured: false,
    keyPoints: [
      '播放量增长与收入增长经常并不同步，这是内容行业最常见的幻觉。',
      '决定存亡的是现金周转周期，而不是单条内容的爆款率。',
      '把内容当成产品来运营的团队，活下来的概率明显更高。',
    ],
    sections: [
      {
        heading: '一、繁荣的数据与沉默的账户',
        paragraphs: [
          '几位受访者都提到过同一个瞬间：某条内容突然爆了，数据后台一片飘红，但当月账上的钱却没有变多。原因是收入结算普遍存在账期，而成本是即时发生的。',
          '当增长只体现在播放量上，团队很容易产生「我们正在变好」的错觉。',
        ],
      },
      {
        heading: '二、现金周转才是生命线',
        paragraphs: [
          '我们对比的八支团队里，活下来的三支有一个共同点：都有稳定的、可预测的经常性收入，比如固定客户的年框内容或垂类社群。它们的内容不一定最爆，但现金流最稳。',
          '相反，完全依赖平台分成的团队，收入波动极大，一次算法调整就可能让整月归零。',
        ],
      },
      {
        heading: '三、把内容当产品的三个动作',
        paragraphs: [
          '第一是明确服务谁，而不是什么都做；第二是建立选题的复用机制，让优质内容能被反复利用；第三是提前规划变现路径，而不是等流量来了才想。',
          '这些动作听起来很朴素，但真正执行的团队并不多，这正是差距的来源。',
        ],
      },
      {
        heading: '四、一点温和的结论',
        paragraphs: [
          '内容行业并不会因为难做就不值得做。真正需要的，是把浪漫的创作冲动和冷静的经营意识放在一起，两者并存，才有可能走得更远。',
        ],
      },
    ],
  },
];